import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';

/**
 * AppToolTip — Design System tooltip wrapper.
 *
 * Defaults: arrow=true, placement='top', enterDelay=300.
 * Pass disableTooltip to render children without a tooltip (useful for conditional display).
 *
 * @param {React.ReactNode}  children        Must be a single React element (MUI Tooltip requirement)
 * @param {React.ReactNode}  title           Tooltip content; falsy value hides the tooltip
 * @param {string}           placement       (default 'top')
 * @param {boolean}          arrow           Show arrow pointer (default true)
 * @param {number}           enterDelay      Delay before showing in ms (default 300)
 * @param {boolean}          disableTooltip  Skip tooltip entirely and render children as-is
 * @param {object}           sx              Extra sx applied to the tooltip box
 */
const AppToolTip = ({
  children,
  title,
  placement = 'top',
  arrow = true,
  enterDelay = 300,
  disableTooltip = false,
  sx,
  ...props
}) => {
  if (disableTooltip || !title) return children;

  return (
    <Tooltip
      title={title}
      placement={placement}
      arrow={arrow}
      enterDelay={enterDelay}
      slotProps={{
        tooltip: {
          sx: {
            fontSize: 12,
            fontWeight: 400,
            ...sx,
          },
        },
      }}
      {...props}
    >
      {children}
    </Tooltip>
  );
};

AppToolTip.propTypes = {
  children: PropTypes.element.isRequired,
  title: PropTypes.node,
  placement: PropTypes.oneOf([
    'top', 'top-start', 'top-end',
    'bottom', 'bottom-start', 'bottom-end',
    'left', 'left-start', 'left-end',
    'right', 'right-start', 'right-end',
  ]),
  arrow: PropTypes.bool,
  enterDelay: PropTypes.number,
  disableTooltip: PropTypes.bool,
  sx: PropTypes.object,
};

export default AppToolTip;
